// ============================================================================
// workbenchStyles.js
// Shared inline style constants for Workbench sub-tabs.
//
// Uses inline style objects (like ConsultantTabHeader.jsx) so the Workbench
// components match the app's existing pattern. No separate CSS file needed.
// ============================================================================

// ── Colours (Atlassian DS tokens) ───────────────────────────────────────────
export const C = {
  N0:   '#FFFFFF',
  N20:  '#F4F5F7',
  N30:  '#EBECF0',
  N40:  '#DFE1E6',
  N50:  '#C1C7D0',
  N80:  '#97A0AF',
  N200: '#6B778C',
  N300: '#5E6C84',
  N400: '#42526E',
  N500: '#344563',
  N800: '#172B4D',
  B50:  '#E9F2FF',
  B75:  '#B3D4FF',
  B100: '#4C9AFF',
  B400: '#0C66E4',
  B500: '#0052CC',
  G50:  '#E3FCEF',
  G200: '#79F2C0',
  G500: '#006644',
  G400: '#22A06B',
  Y50:  '#FFF8F0',
  Y75:  '#FFE2B8',
  Y400: '#FF8B00',
  R50:  '#FFEBE6',
  R75:  '#FF8F73',
  R400: '#BF2600',
  P50:  '#F3F0FF',
  P75:  '#C0B6F2',
  P400: '#5E4DB2',
  P500: '#6554C0',
};

// ── Typography ──────────────────────────────────────────────────────────────
export const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ── Shared component styles ─────────────────────────────────────────────────

export const ROOT = {
  height: '100%',
  overflow: 'auto',
  padding: '12px 16px',
  background: C.N20,
  color: C.N800,
  fontFamily: FONT,
};

export const SHELL = {
  maxWidth: 1440,
  margin: '0 auto',
};

export const CARD = {
  background: C.N0,
  border: `1px solid ${C.N40}`,
  borderRadius: 12,
  boxShadow: '0 1px 2px rgba(9,30,66,.06)',
  overflow: 'hidden',
  marginBottom: 10,
};

export const CARD_HEADER = {
  padding: '10px 14px',
  borderBottom: `1px solid ${C.N30}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

export const CARD_TITLE_ROW = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
};

export const CARD_TITLE = {
  fontWeight: 780,
  color: C.N800,
  fontSize: 14,
  margin: 0,
};

export const CARD_BODY = {
  padding: '10px 14px',
};

export const PILL = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 22,
  height: 22,
  padding: '0 7px',
  borderRadius: 999,
  background: C.B50,
  color: C.B400,
  fontSize: 12,
  fontWeight: 800,
};

export const BUTTON_PRIMARY = {
  height: 34,
  border: `1px solid ${C.B400}`,
  background: C.B400,
  color: '#fff',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 12,
  padding: '0 12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
};

export const BUTTON_SECONDARY = {
  ...BUTTON_PRIMARY,
  background: C.N0,
  color: C.B400,
  borderColor: C.B75,
};

export const ICON_BUTTON = {
  height: 32,
  minWidth: 32,
  border: `1px solid ${C.N40}`,
  background: C.N0,
  color: C.N400,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

export const ICON_BUTTON_PRIMARY = {
  ...ICON_BUTTON,
  borderColor: C.B75,
  color: C.B400,
  background: '#F4F8FF',
};

// ── Form controls ───────────────────────────────────────────────────────────

export const INPUT = {
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${C.N50}`,
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  color: C.N800,
  background: C.N0,
  fontFamily: FONT,
};

export const TEXTAREA = {
  ...INPUT,
  resize: 'vertical',
  minHeight: 72,
};

export const SELECT = {
  ...INPUT,
};

export const FIELD = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
};

export const FIELD_LABEL = {
  fontSize: 12,
  color: C.N300,
  fontWeight: 700,
};

// ── Status pill ─────────────────────────────────────────────────────────────

const STATUS_TONES = {
  neutral: { bg: C.N20,  color: C.N400, border: C.N40 },
  blue:    { bg: C.B50,  color: C.B400, border: C.B75 },
  green:   { bg: C.G50,  color: C.G500, border: C.G200 },
  red:     { bg: C.R50,  color: C.R400, border: C.R75 },
  purple:  { bg: C.P50,  color: C.P400, border: C.P75 },
};

export const statusPillStyle = (tone = 'neutral') => {
  const t = STATUS_TONES[tone] || STATUS_TONES.neutral;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: 24,
    padding: '0 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: `1px solid ${t.border}`,
    background: t.bg,
    color: t.color,
  };
};

// ── List / accordion ────────────────────────────────────────────────────────

export const LIST = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

export const LIST_ITEM = {
  border: `1px solid ${C.N40}`,
  borderRadius: 12,
  background: C.N0,
  overflow: 'hidden',
};

export const LIST_SUMMARY = {
  display: 'grid',
  gridTemplateColumns: '28px minmax(0,1fr) auto auto',
  gap: 12,
  alignItems: 'center',
  padding: '12px 14px',
  cursor: 'pointer',
};

export const LIST_SUMMARY_HOVER = {
  background: '#FAFBFC',
};

export const ITEM_TITLE = {
  fontSize: 14,
  fontWeight: 700,
  color: C.N800,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const ITEM_SUBTITLE = {
  marginTop: 3,
  fontSize: 12,
  color: C.N300,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const CHIP_ROW = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  justifyContent: 'flex-end',
};

export const ITEM_ACTIONS = {
  display: 'flex',
  gap: 8,
};

export const ITEM_BODY = {
  borderTop: `1px solid ${C.N30}`,
  padding: 14,
  background: '#FCFDFE',
};

export const FORM_GRID = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 10,
};

export const ITEM_GRID_FINDING = {
  display: 'grid',
  gridTemplateColumns: '1.35fr 1fr 1fr 1fr',
  gap: 10,
  marginBottom: 12,
};

export const ITEM_GRID_ACTION = {
  display: 'grid',
  gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
  gap: 10,
  marginBottom: 12,
};

export const LINKED_BADGE = {
  background: '#F4F8FF',
  border: `1px solid ${C.B75}`,
  color: C.N800,
  borderRadius: 8,
  padding: '9px 10px',
  fontSize: 12,
  marginBottom: 10,
};

export const EMPTY = {
  border: `1px dashed ${C.N50}`,
  background: '#FAFBFC',
  color: C.N300,
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 13,
};

export const DIVIDER_NOTE = {
  color: C.N300,
  fontSize: 12,
  lineHeight: 1.45,
};

// ── Dashboard-specific ──────────────────────────────────────────────────────

export const STAT_GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))',
  gap: 12,
};

const STAT_TONES = {
  blue:   { top: C.B400, bg: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' },
  orange: { top: C.Y400, bg: 'linear-gradient(180deg, #FFF8F0 0%, #FFFFFF 100%)' },
  green:  { top: C.G400, bg: 'linear-gradient(180deg, #F2FCF6 0%, #FFFFFF 100%)' },
  purple: { top: C.P500, bg: 'linear-gradient(180deg, #F7F5FF 0%, #FFFFFF 100%)' },
};

export const statCardStyle = (tone = 'blue') => {
  const t = STAT_TONES[tone] || STAT_TONES.blue;
  return {
    background: t.bg,
    border: `1px solid ${C.N40}`,
    borderTopWidth: 4,
    borderTopColor: t.top,
    borderRadius: 12,
    padding: '14px 16px',
    minHeight: 90,
    boxShadow: '0 1px 2px rgba(9,30,66,.05)',
    cursor: 'pointer',
  };
};

export const STAT_LABEL = {
  fontSize: 13,
  color: C.N400,
  fontWeight: 700,
};

export const STAT_VALUE = {
  fontSize: 34,
  color: C.N800,
  fontWeight: 800,
  marginTop: 6,
  lineHeight: 1,
};

export const STAT_NOTE = {
  fontSize: 12,
  color: C.N300,
  marginTop: 6,
  lineHeight: 1.35,
};

// ── Outputs grid ────────────────────────────────────────────────────────────

export const OUTPUTS_GRID = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

export const OUTPUT_CARD = {
  border: `1px solid ${C.N40}`,
  borderRadius: 12,
  background: C.N0,
  padding: 14,
};

export const OUTPUT_TOP = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 14,
};

export const OUTPUT_TITLE = {
  fontSize: 15,
  fontWeight: 700,
  color: C.N800,
  margin: 0,
};

export const OUTPUT_DESC = {
  marginTop: 5,
  color: C.N300,
  fontSize: 12,
  lineHeight: 1.45,
};

// ── Table view (compact mode for findings/actions) ──────────────────────────

export const TABLE = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontSize: 13,
};

export const TH = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 11,
  fontWeight: 700,
  color: C.N300,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: `2px solid ${C.N30}`,
  background: C.N0,
  whiteSpace: 'nowrap',
};

export const TD = {
  padding: '10px 10px',
  borderBottom: `1px solid ${C.N30}`,
  color: C.N800,
  verticalAlign: 'middle',
};

export const TD_TITLE = {
  fontWeight: 600,
  color: C.B500,
  cursor: 'pointer',
};

// ── Quick-action row (dashboard recent items) ───────────────────────────────

export const RECENT_ROW = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottom: `1px solid ${C.N30}`,
  cursor: 'pointer',
  gap: 12,
};

export const RECENT_ROW_LAST = {
  ...RECENT_ROW,
  borderBottom: 'none',
};

// ── Engagement summary (read-only on dashboard) ─────────────────────────────

export const SUMMARY_ROW = {
  display: 'flex',
  gap: 8,
  padding: '4px 0',
  fontSize: 13,
  lineHeight: 1.4,
};

export const SUMMARY_LABEL = {
  fontWeight: 700,
  color: C.N300,
  minWidth: 90,
  flexShrink: 0,
  fontSize: 12,
};

export const SUMMARY_VALUE = {
  color: C.N800,
  fontSize: 13,
};

// ── Prompt banner (dismissable) ─────────────────────────────────────────────

export const PROMPT_BANNER = {
  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
  border: `1px solid ${C.B75}`,
  borderRadius: 8,
  padding: '8px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

export const PROMPT_TEXT = {
  fontSize: 13,
  color: C.N400,
  fontWeight: 500,
};

export const PROMPT_LINK = {
  fontSize: 13,
  color: C.B400,
  fontWeight: 700,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
  textDecoration: 'underline',
  fontFamily: FONT,
};
